// TEMPORARY dummy player fixtures for the Player Goals/Cards Movement toggle.
//
// The Player Movement `.player-toggle` (which players are OFFERED) is derived on
// the frontend by `playerControlOptions.ts` from these player market + movement
// payloads. Unlike every other widget's data (fetched from app-api), a control's
// option list is needed synchronously at render time, and the movement widget's
// own fetched payload isn't available to the control — so the list needs a
// frontend source. Until M2 Slice 6 this came from the legacy `data.js` dummy
// layer; that layer was deleted, so the four player slices are rehomed here.
//
// This is a STOPGAP. The real source is a data server — the lineups / player feed
// (M3). Wire the toggle list to it when that server lands and delete this file.
// Tracked in `bento-planning/data-servers-plan.md` §9 (frontend integration pickups).
//
// A faithful structural port of the `data.js` player slices — helper functions +
// literals verbatim, only the accumulator target changed. Kept small (these four
// slices only) so the app bundle ships just the player list it needs, not the
// whole dummy dataset.

import type { PlayerMarketData, PlayerMovementData } from "@bento/shared";

// ─── Movement helpers (points over time) ─────────────────────────────────────
const _gmT = ["72h", "48h", "36h", "24h", "12h", "6h", "3h", "1h", "NOW"];
const _gmS = (o: number[]) => _gmT.map((t, i) => ({ t, o: o[i]! }));
const _mvScale = (obj: Record<string, { t: string; o: number }[]>, x: number) => {
  const r: Record<string, { t: string; o: number }[]> = {};
  Object.keys(obj).forEach((k) => {
    r[k] = obj[k]!.map((d) => ({ t: d.t, o: Math.round(d.o * x * 100) / 100 }));
  });
  return r;
};
const _mgFix = (at: Record<string, { t: string; o: number }[]>) => ({
  Anytime: at,
  First: _mvScale(at, 2.8),
  "2+": _mvScale(at, 4.5),
});
const _mcFix = (at: Record<string, { t: string; o: number }[]>) => ({
  Anytime: at,
  First: _mvScale(at, 2.3),
});

// ─── Market helpers (per-book rows) ──────────────────────────────────────────
interface PrRow {
  player: string;
  team: string;
  model: number;
  Pinnacle: number;
  Betfair: number;
  Ladbrokes: number;
  "Paddy Power": number;
}
const _pr = (
  p: string,
  t: string,
  m: number,
  pn: number,
  bf: number,
  ld: number,
  pp: number
): PrRow => ({
  player: p,
  team: t,
  model: m,
  Pinnacle: pn,
  Betfair: bf,
  Ladbrokes: ld,
  "Paddy Power": pp,
});
const _prScale = (rows: PrRow[], x: number): PrRow[] =>
  rows.map((r) => {
    const f = (v: number) => Math.round(v * x * 10) / 10;
    return _pr(
      r.player,
      r.team,
      f(r.model),
      f(r.Pinnacle),
      f(r.Betfair),
      f(r.Ladbrokes),
      f(r["Paddy Power"])
    );
  });
const _prSort = (rows: PrRow[]): PrRow[] =>
  rows.slice().sort((a, b) => a.model - b.model);

// ─── Player goals market (fixture → scope → market type → rows) ──────────────
function _mkGoals(ars: PrRow[], eve: PrRow[]) {
  const m = _prSort(ars.concat(eve));
  return {
    Match: { Anytime: m, First: _prScale(m, 2.8), "2+": _prScale(m, 4.5) },
    ARS: {
      Anytime: _prSort(ars),
      First: _prScale(_prSort(ars), 2.8),
      "2+": _prScale(_prSort(ars), 4.5),
    },
    EVE: {
      Anytime: _prSort(eve),
      First: _prScale(_prSort(eve), 2.8),
      "2+": _prScale(_prSort(eve), 4.5),
    },
  };
}

export const playerGoalsMarketData = {
  "Arsenal v Everton": _mkGoals(
    [_pr("Havertz", "ARS", 3.55, 3.5, 3.6, 3.5, 3.55), _pr("Saka", "ARS", 3.8, 4.05, 3.9, 3.75, 3.85), _pr("Martinelli", "ARS", 4.2, 4.15, 4.25, 4.05, 4.45), _pr("Trossard", "ARS", 5.0, 5.3, 5.1, 4.9, 5.0), _pr("Jesus", "ARS", 5.5, 5.45, 5.7, 5.3, 5.5), _pr("Odegaard", "ARS", 7.0, 6.9, 7.1, 7.5, 7.0)],
    [_pr("Calvert-Lewin", "EVE", 3.2, 3.2, 3.3, 3.15, 3.45), _pr("Beto", "EVE", 5.0, 5.3, 5.0, 4.85, 5.0), _pr("McNeil", "EVE", 7.0, 7.0, 7.2, 6.8, 7.0), _pr("Doucoure", "EVE", 9.0, 8.9, 9.2, 9.8, 9.0), _pr("Ndiaye", "EVE", 10.0, 10.0, 10.2, 9.8, 10.8)]
  ),
  "Liverpool v Manchester City": _mkGoals(
    [_pr("Salah", "ARS", 2.6, 2.6, 2.75, 2.55, 2.65), _pr("Nunez", "ARS", 3.0, 3.2, 3.05, 2.95, 3.0), _pr("Jota", "ARS", 4.0, 3.95, 4.0, 3.85, 4.2), _pr("Diaz", "ARS", 4.5, 4.45, 4.5, 4.75, 4.45), _pr("Mac Allister", "ARS", 7.0, 7.4, 7.0, 6.8, 7.0)],
    [_pr("Haaland", "EVE", 1.85, 1.9, 1.95, 1.85, 2.05), _pr("Foden", "EVE", 3.5, 3.5, 3.6, 3.4, 3.55), _pr("De Bruyne", "EVE", 4.0, 4.2, 4.05, 3.9, 4.0), _pr("Doku", "EVE", 5.0, 4.9, 5.0, 5.3, 4.95), _pr("Gvardiol", "EVE", 8.0, 8.0, 8.2, 7.8, 8.6)]
  ),
  "Chelsea v Tottenham": _mkGoals(
    [_pr("Palmer", "ARS", 2.8, 2.95, 2.85, 2.75, 2.8), _pr("Jackson", "ARS", 3.2, 3.2, 3.3, 3.1, 3.25), _pr("Nkunku", "ARS", 4.5, 4.4, 4.5, 4.35, 4.7), _pr("Sterling", "ARS", 5.0, 4.9, 5.1, 5.4, 5.0), _pr("Mudryk", "ARS", 6.0, 6.3, 6.1, 5.8, 6.0)],
    [_pr("Son", "EVE", 2.7, 2.7, 2.8, 2.65, 2.75), _pr("Richarlison", "EVE", 4.0, 3.95, 4.0, 3.85, 4.2), _pr("Kulusevski", "EVE", 4.2, 4.45, 4.2, 4.1, 4.25), _pr("Maddison", "EVE", 5.0, 4.9, 5.1, 5.4, 5.0), _pr("Johnson", "EVE", 5.5, 5.5, 5.6, 5.4, 5.5)]
  ),
  "Manchester United v Newcastle": _mkGoals(
    [_pr("Hojlund", "ARS", 3.2, 3.15, 3.2, 3.1, 3.4), _pr("Rashford", "ARS", 3.8, 4.0, 3.8, 3.7, 3.8), _pr("Fernandes", "ARS", 5.0, 5.0, 5.2, 4.9, 5.0), _pr("Garnacho", "ARS", 5.5, 5.4, 5.5, 5.8, 5.4), _pr("Mount", "ARS", 6.0, 5.9, 6.1, 5.8, 6.4)],
    [_pr("Isak", "EVE", 2.6, 2.6, 2.75, 2.55, 2.65), _pr("Gordon", "EVE", 4.0, 4.2, 4.05, 3.9, 4.0), _pr("Wilson", "EVE", 5.0, 4.9, 5.0, 4.8, 5.3), _pr("Almiron", "EVE", 6.0, 5.9, 6.1, 6.5, 6.0), _pr("Murphy", "EVE", 7.0, 7.0, 7.2, 6.8, 7.0)]
  ),
  "Aston Villa v Brighton": _mkGoals(
    [_pr("Watkins", "ARS", 2.8, 3.0, 2.85, 2.75, 2.8), _pr("Diaby", "ARS", 4.5, 4.45, 4.5, 4.35, 4.7), _pr("Bailey", "ARS", 5.0, 5.0, 5.2, 4.9, 5.0), _pr("McGinn", "ARS", 7.0, 6.9, 7.0, 7.4, 6.9), _pr("Tielemans", "ARS", 8.0, 8.4, 8.2, 7.8, 8.0)],
    [_pr("Mitoma", "EVE", 3.5, 3.45, 3.55, 3.35, 3.7), _pr("Welbeck", "EVE", 4.0, 4.0, 4.1, 3.9, 4.0), _pr("Pedro", "EVE", 4.5, 4.7, 4.5, 4.35, 4.5), _pr("Enciso", "EVE", 5.5, 5.4, 5.5, 5.8, 5.4), _pr("Gross", "EVE", 6.9, 6.9, 7.0, 6.8, 7.3)]
  ),
} as unknown as PlayerMarketData;

// ─── Player cards market (fixture → scope → market type → rows) ──────────────
function _mkCards(ars: PrRow[], eve: PrRow[]) {
  const m = _prSort(ars.concat(eve));
  return {
    Match: { Anytime: m, First: _prScale(m, 2.3) },
    ARS: { Anytime: _prSort(ars), First: _prScale(_prSort(ars), 2.3) },
    EVE: { Anytime: _prSort(eve), First: _prScale(_prSort(eve), 2.3) },
  };
}

export const playerCardsMarketData = {
  "Arsenal v Everton": _mkCards(
    [_pr("Rice", "ARS", 2.75, 2.9, 2.8, 2.7, 2.8), _pr("Timber", "ARS", 3.5, 3.5, 3.6, 3.4, 3.5), _pr("Havertz", "ARS", 4.0, 3.95, 4.05, 3.9, 4.25), _pr("White", "ARS", 4.2, 4.15, 4.25, 4.5, 4.2), _pr("Saka", "ARS", 4.5, 4.5, 4.6, 4.4, 4.55), _pr("Odegaard", "ARS", 5.0, 5.3, 5.1, 4.9, 5.0)],
    [_pr("Doucoure", "EVE", 2.5, 2.5, 2.55, 2.45, 2.7), _pr("Tarkowski", "EVE", 3.0, 3.0, 3.1, 2.95, 3.0), _pr("Branthwaite", "EVE", 3.5, 3.7, 3.55, 3.4, 3.5), _pr("Patterson", "EVE", 4.0, 3.95, 4.05, 4.3, 4.0), _pr("McNeil", "EVE", 4.5, 4.45, 4.55, 4.4, 4.75)]
  ),
  "Liverpool v Manchester City": _mkCards(
    [_pr("Gravenberch", "ARS", 3.2, 3.2, 3.3, 3.1, 3.25), _pr("Szoboszlai", "ARS", 3.5, 3.7, 3.55, 3.4, 3.55), _pr("Robertson", "ARS", 4.0, 3.95, 4.05, 3.9, 4.2), _pr("Konate", "ARS", 4.5, 4.45, 4.55, 4.75, 4.5), _pr("Salah", "ARS", 5.0, 5.3, 5.1, 4.9, 5.0)],
    [_pr("Kovacic", "EVE", 3.0, 3.0, 3.1, 2.95, 3.05), _pr("Stones", "EVE", 3.5, 3.45, 3.55, 3.4, 3.7), _pr("Silva", "EVE", 3.8, 4.0, 3.85, 3.7, 3.8), _pr("De Bruyne", "EVE", 4.0, 3.95, 4.05, 4.3, 4.0), _pr("Gvardiol", "EVE", 4.5, 4.5, 4.6, 4.4, 4.5)]
  ),
  "Chelsea v Tottenham": _mkCards(
    [_pr("Caicedo", "ARS", 3.0, 2.95, 3.05, 2.95, 3.2), _pr("Enzo", "ARS", 3.5, 3.5, 3.6, 3.4, 3.5), _pr("Badiashile", "ARS", 3.8, 4.0, 3.85, 3.7, 3.8), _pr("Palmer", "ARS", 4.0, 3.95, 4.05, 4.3, 4.0), _pr("Cucurella", "ARS", 4.5, 4.45, 4.55, 4.4, 4.75)],
    [_pr("Bissouma", "EVE", 2.75, 2.75, 2.85, 2.7, 2.8), _pr("Romero", "EVE", 3.0, 3.2, 3.05, 2.95, 3.05), _pr("Bentancur", "EVE", 3.2, 3.15, 3.25, 3.1, 3.45), _pr("Porro", "EVE", 3.5, 3.5, 3.6, 3.4, 3.5), _pr("Sarr", "EVE", 4.0, 3.95, 4.05, 4.3, 4.0)]
  ),
  "Manchester United v Newcastle": _mkCards(
    [_pr("Casemiro", "ARS", 2.75, 2.95, 2.8, 2.7, 2.8), _pr("Fernandes", "ARS", 3.5, 3.5, 3.6, 3.4, 3.5), _pr("Maguire", "ARS", 3.8, 3.75, 3.85, 3.7, 4.0), _pr("Dalot", "ARS", 4.0, 4.25, 4.05, 3.9, 4.0), _pr("Lindelof", "ARS", 4.5, 4.5, 4.6, 4.4, 4.5)],
    [_pr("Longstaff", "EVE", 3.0, 2.95, 3.05, 3.25, 3.0), _pr("Guimaraes", "EVE", 3.2, 3.15, 3.25, 3.1, 3.45), _pr("Burn", "EVE", 3.5, 3.5, 3.6, 3.4, 3.5), _pr("Trippier", "EVE", 4.0, 4.25, 4.05, 3.9, 4.0), _pr("Botman", "EVE", 4.5, 4.45, 4.55, 4.75, 4.5)]
  ),
  "Aston Villa v Brighton": _mkCards(
    [_pr("McGinn", "ARS", 3.0, 2.95, 3.05, 2.95, 3.2), _pr("Douglas Luiz", "ARS", 3.2, 3.2, 3.3, 3.1, 3.25), _pr("Mings", "ARS", 3.8, 4.0, 3.85, 3.7, 3.8), _pr("Digne", "ARS", 4.0, 3.95, 4.05, 4.3, 4.0), _pr("Carlos", "ARS", 4.5, 4.5, 4.6, 4.4, 4.5)],
    [_pr("Dunk", "EVE", 3.0, 3.2, 3.05, 2.95, 3.05), _pr("Dahoud", "EVE", 3.2, 3.15, 3.25, 3.1, 3.45), _pr("Milner", "EVE", 3.5, 3.5, 3.6, 3.4, 3.5), _pr("Veltman", "EVE", 4.0, 3.95, 4.05, 4.3, 4.0), _pr("Estupinan", "EVE", 4.5, 4.45, 4.55, 4.4, 4.75)]
  ),
} as unknown as PlayerMarketData;

// ─── Player goals movement (fixture → market type → player → points) ─────────
export const playerGoalsMovementData = {
  "Arsenal v Everton": _mgFix({
    "Calvert-Lewin": _gmS([3.5, 3.45, 3.4, 3.35, 3.3, 3.28, 3.25, 3.25, 3.25]),
    Havertz: _gmS([3.9, 3.8, 3.75, 3.7, 3.6, 3.55, 3.52, 3.5, 3.5]),
    Saka: _gmS([4.2, 4.15, 4.1, 4.0, 3.95, 3.9, 3.85, 3.8, 3.8]),
    Martinelli: _gmS([4.5, 4.4, 4.35, 4.3, 4.25, 4.2, 4.2, 4.2, 4.2]),
    Beto: _gmS([5.2, 5.1, 5.05, 5.05, 5.0, 5.0, 5.0, 5.0, 5.0]),
    Trossard: _gmS([5.0, 5.0, 5.0, 5.05, 5.05, 5.0, 5.0, 5.0, 5.0]),
    Jesus: _gmS([5.8, 5.7, 5.6, 5.6, 5.55, 5.5, 5.5, 5.5, 5.5]),
    McNeil: _gmS([7.2, 7.15, 7.1, 7.05, 7.0, 7.0, 7.0, 7.0, 7.0]),
    Odegaard: _gmS([7.5, 7.2, 7.1, 7.0, 6.9, 6.85, 6.8, 6.8, 6.8]),
    Doucoure: _gmS([9.5, 9.2, 9.1, 9.0, 9.0, 9.0, 9.0, 9.0, 9.0]),
    Ndiaye: _gmS([10.5, 10.2, 10.1, 10.0, 10.0, 10.0, 10.0, 10.0, 10.0]),
  }),
  "Liverpool v Manchester City": _mgFix({
    Haaland: _gmS([2.1, 2.05, 2.0, 1.98, 1.95, 1.93, 1.92, 1.9, 1.9]),
    Salah: _gmS([2.8, 2.75, 2.7, 2.68, 2.65, 2.63, 2.62, 2.6, 2.6]),
    Nunez: _gmS([3.2, 3.15, 3.1, 3.08, 3.05, 3.03, 3.02, 3.0, 3.0]),
    Foden: _gmS([3.7, 3.65, 3.6, 3.58, 3.55, 3.53, 3.52, 3.5, 3.5]),
    Jota: _gmS([4.2, 4.15, 4.1, 4.08, 4.05, 4.02, 4.02, 4.0, 4.0]),
    "De Bruyne": _gmS([4.2, 4.15, 4.1, 4.05, 4.02, 4.02, 4.0, 4.0, 4.0]),
    Diaz: _gmS([4.7, 4.65, 4.6, 4.55, 4.52, 4.5, 4.5, 4.5, 4.5]),
    Doku: _gmS([5.2, 5.15, 5.1, 5.08, 5.05, 5.02, 5.0, 5.0, 5.0]),
    "Mac Allister": _gmS([7.2, 7.15, 7.1, 7.05, 7.02, 7.0, 7.0, 7.0, 7.0]),
    Gvardiol: _gmS([8.5, 8.3, 8.2, 8.1, 8.05, 8.0, 8.0, 8.0, 8.0]),
  }),
  "Chelsea v Tottenham": _mgFix({
    Son: _gmS([2.9, 2.85, 2.8, 2.78, 2.75, 2.73, 2.72, 2.7, 2.7]),
    Palmer: _gmS([3.0, 2.95, 2.9, 2.88, 2.85, 2.83, 2.82, 2.8, 2.8]),
    Jackson: _gmS([3.4, 3.35, 3.3, 3.28, 3.25, 3.23, 3.22, 3.2, 3.2]),
    Richarlison: _gmS([4.2, 4.15, 4.1, 4.05, 4.02, 4.0, 4.0, 4.0, 4.0]),
    Kulusevski: _gmS([4.4, 4.35, 4.3, 4.28, 4.25, 4.22, 4.22, 4.2, 4.2]),
    Nkunku: _gmS([4.7, 4.65, 4.6, 4.55, 4.52, 4.5, 4.5, 4.5, 4.5]),
    Maddison: _gmS([5.2, 5.15, 5.1, 5.05, 5.02, 5.0, 5.0, 5.0, 5.0]),
    Sterling: _gmS([5.2, 5.15, 5.1, 5.05, 5.02, 5.0, 5.0, 5.0, 5.0]),
    Johnson: _gmS([5.7, 5.65, 5.6, 5.55, 5.52, 5.5, 5.5, 5.5, 5.5]),
    Mudryk: _gmS([6.2, 6.15, 6.1, 6.05, 6.02, 6.0, 6.0, 6.0, 6.0]),
  }),
  "Manchester United v Newcastle": _mgFix({
    Isak: _gmS([2.8, 2.75, 2.7, 2.68, 2.65, 2.63, 2.62, 2.6, 2.6]),
    Hojlund: _gmS([3.4, 3.35, 3.3, 3.28, 3.25, 3.22, 3.22, 3.2, 3.2]),
    Rashford: _gmS([4.0, 3.95, 3.9, 3.88, 3.85, 3.83, 3.82, 3.8, 3.8]),
    Gordon: _gmS([4.2, 4.15, 4.1, 4.05, 4.02, 4.0, 4.0, 4.0, 4.0]),
    Fernandes: _gmS([5.2, 5.15, 5.1, 5.05, 5.02, 5.0, 5.0, 5.0, 5.0]),
    Wilson: _gmS([5.2, 5.15, 5.1, 5.05, 5.02, 5.0, 5.0, 5.0, 5.0]),
    Garnacho: _gmS([5.7, 5.65, 5.6, 5.55, 5.52, 5.5, 5.5, 5.5, 5.5]),
    Almiron: _gmS([6.2, 6.15, 6.1, 6.05, 6.02, 6.0, 6.0, 6.0, 6.0]),
    Mount: _gmS([6.2, 6.15, 6.1, 6.05, 6.02, 6.0, 6.0, 6.0, 6.0]),
    Murphy: _gmS([7.2, 7.15, 7.1, 7.05, 7.02, 7.0, 7.0, 7.0, 7.0]),
  }),
  "Aston Villa v Brighton": _mgFix({
    Watkins: _gmS([3.0, 2.95, 2.92, 2.88, 2.85, 2.83, 2.82, 2.8, 2.8]),
    Mitoma: _gmS([3.7, 3.65, 3.6, 3.58, 3.55, 3.52, 3.52, 3.5, 3.5]),
    Welbeck: _gmS([4.2, 4.15, 4.1, 4.05, 4.02, 4.0, 4.0, 4.0, 4.0]),
    Pedro: _gmS([4.7, 4.65, 4.6, 4.55, 4.52, 4.5, 4.5, 4.5, 4.5]),
    Diaby: _gmS([4.7, 4.65, 4.6, 4.55, 4.52, 4.5, 4.5, 4.5, 4.5]),
    Bailey: _gmS([5.2, 5.15, 5.1, 5.05, 5.02, 5.0, 5.0, 5.0, 5.0]),
    Enciso: _gmS([5.7, 5.65, 5.6, 5.55, 5.52, 5.5, 5.5, 5.5, 5.5]),
    McGinn: _gmS([7.2, 7.15, 7.1, 7.05, 7.02, 7.0, 7.0, 7.0, 7.0]),
    Gross: _gmS([7.2, 7.15, 7.1, 7.05, 7.02, 7.0, 7.0, 7.0, 7.0]),
    Tielemans: _gmS([8.2, 8.15, 8.1, 8.05, 8.02, 8.0, 8.0, 8.0, 8.0]),
  }),
} as unknown as PlayerMovementData;

// ─── Player cards movement (fixture → market type → player → points) ─────────
export const playerCardsMovementData = {
  "Arsenal v Everton": _mcFix({
    Doucoure: _gmS([2.7, 2.65, 2.62, 2.6, 2.56, 2.54, 2.52, 2.5, 2.5]),
    Rice: _gmS([2.9, 2.85, 2.82, 2.8, 2.78, 2.77, 2.76, 2.75, 2.75]),
    Tarkowski: _gmS([3.15, 3.1, 3.08, 3.05, 3.03, 3.02, 3.02, 3.0, 3.0]),
    Timber: _gmS([3.65, 3.6, 3.58, 3.55, 3.52, 3.52, 3.5, 3.5, 3.5]),
    Branthwaite: _gmS([3.65, 3.6, 3.58, 3.55, 3.52, 3.52, 3.5, 3.5, 3.5]),
    Havertz: _gmS([4.15, 4.1, 4.08, 4.05, 4.03, 4.02, 4.02, 4.0, 4.0]),
    White: _gmS([4.35, 4.3, 4.28, 4.25, 4.23, 4.22, 4.22, 4.2, 4.2]),
    Patterson: _gmS([4.15, 4.1, 4.08, 4.05, 4.03, 4.02, 4.02, 4.0, 4.0]),
    Saka: _gmS([4.65, 4.6, 4.58, 4.55, 4.53, 4.52, 4.52, 4.5, 4.5]),
    McNeil: _gmS([4.65, 4.6, 4.58, 4.55, 4.53, 4.52, 4.52, 4.5, 4.5]),
    Odegaard: _gmS([5.2, 5.15, 5.1, 5.05, 5.03, 5.02, 5.02, 5.0, 5.0]),
  }),
  "Liverpool v Manchester City": _mcFix({
    Kovacic: _gmS([3.15, 3.1, 3.08, 3.05, 3.03, 3.02, 3.02, 3.0, 3.0]),
    Gravenberch: _gmS([3.35, 3.3, 3.28, 3.25, 3.23, 3.22, 3.22, 3.2, 3.2]),
    Stones: _gmS([3.65, 3.6, 3.58, 3.55, 3.52, 3.52, 3.5, 3.5, 3.5]),
    Szoboszlai: _gmS([3.65, 3.6, 3.58, 3.55, 3.52, 3.52, 3.5, 3.5, 3.5]),
    Silva: _gmS([3.95, 3.9, 3.88, 3.85, 3.83, 3.82, 3.82, 3.8, 3.8]),
    Robertson: _gmS([4.15, 4.1, 4.08, 4.05, 4.03, 4.02, 4.02, 4.0, 4.0]),
    "De Bruyne": _gmS([4.15, 4.1, 4.08, 4.05, 4.03, 4.02, 4.02, 4.0, 4.0]),
    Konate: _gmS([4.65, 4.6, 4.58, 4.55, 4.53, 4.52, 4.52, 4.5, 4.5]),
    Gvardiol: _gmS([4.65, 4.6, 4.58, 4.55, 4.53, 4.52, 4.52, 4.5, 4.5]),
    Salah: _gmS([5.2, 5.15, 5.1, 5.05, 5.03, 5.02, 5.02, 5.0, 5.0]),
  }),
  "Chelsea v Tottenham": _mcFix({
    Bissouma: _gmS([2.9, 2.85, 2.82, 2.8, 2.78, 2.77, 2.76, 2.75, 2.75]),
    Caicedo: _gmS([3.15, 3.1, 3.08, 3.05, 3.03, 3.02, 3.02, 3.0, 3.0]),
    Romero: _gmS([3.15, 3.1, 3.08, 3.05, 3.03, 3.02, 3.02, 3.0, 3.0]),
    Bentancur: _gmS([3.35, 3.3, 3.28, 3.25, 3.23, 3.22, 3.22, 3.2, 3.2]),
    Porro: _gmS([3.65, 3.6, 3.58, 3.55, 3.52, 3.52, 3.5, 3.5, 3.5]),
    Enzo: _gmS([3.65, 3.6, 3.58, 3.55, 3.52, 3.52, 3.5, 3.5, 3.5]),
    Badiashile: _gmS([3.95, 3.9, 3.88, 3.85, 3.83, 3.82, 3.82, 3.8, 3.8]),
    Palmer: _gmS([4.15, 4.1, 4.08, 4.05, 4.03, 4.02, 4.02, 4.0, 4.0]),
    Sarr: _gmS([4.15, 4.1, 4.08, 4.05, 4.03, 4.02, 4.02, 4.0, 4.0]),
    Cucurella: _gmS([4.65, 4.6, 4.58, 4.55, 4.53, 4.52, 4.52, 4.5, 4.5]),
  }),
  "Manchester United v Newcastle": _mcFix({
    Casemiro: _gmS([2.9, 2.85, 2.82, 2.8, 2.78, 2.77, 2.76, 2.75, 2.75]),
    Longstaff: _gmS([3.15, 3.1, 3.08, 3.05, 3.03, 3.02, 3.02, 3.0, 3.0]),
    Guimaraes: _gmS([3.35, 3.3, 3.28, 3.25, 3.23, 3.22, 3.22, 3.2, 3.2]),
    Fernandes: _gmS([3.65, 3.6, 3.58, 3.55, 3.52, 3.52, 3.5, 3.5, 3.5]),
    Burn: _gmS([3.65, 3.6, 3.58, 3.55, 3.52, 3.52, 3.5, 3.5, 3.5]),
    Maguire: _gmS([3.95, 3.9, 3.88, 3.85, 3.83, 3.82, 3.82, 3.8, 3.8]),
    Dalot: _gmS([4.15, 4.1, 4.08, 4.05, 4.03, 4.02, 4.02, 4.0, 4.0]),
    Trippier: _gmS([4.15, 4.1, 4.08, 4.05, 4.03, 4.02, 4.02, 4.0, 4.0]),
    Lindelof: _gmS([4.65, 4.6, 4.58, 4.55, 4.53, 4.52, 4.52, 4.5, 4.5]),
    Botman: _gmS([4.65, 4.6, 4.58, 4.55, 4.53, 4.52, 4.52, 4.5, 4.5]),
  }),
  "Aston Villa v Brighton": _mcFix({
    Dunk: _gmS([3.15, 3.1, 3.08, 3.05, 3.03, 3.02, 3.02, 3.0, 3.0]),
    McGinn: _gmS([3.15, 3.1, 3.08, 3.05, 3.03, 3.02, 3.02, 3.0, 3.0]),
    Dahoud: _gmS([3.35, 3.3, 3.28, 3.25, 3.23, 3.22, 3.22, 3.2, 3.2]),
    "Douglas Luiz": _gmS([3.35, 3.3, 3.28, 3.25, 3.23, 3.22, 3.22, 3.2, 3.2]),
    Milner: _gmS([3.65, 3.6, 3.58, 3.55, 3.52, 3.52, 3.5, 3.5, 3.5]),
    Mings: _gmS([3.95, 3.9, 3.88, 3.85, 3.83, 3.82, 3.82, 3.8, 3.8]),
    Digne: _gmS([4.15, 4.1, 4.08, 4.05, 4.03, 4.02, 4.02, 4.0, 4.0]),
    Veltman: _gmS([4.15, 4.1, 4.08, 4.05, 4.03, 4.02, 4.02, 4.0, 4.0]),
    Carlos: _gmS([4.65, 4.6, 4.58, 4.55, 4.53, 4.52, 4.52, 4.5, 4.5]),
    Estupinan: _gmS([4.65, 4.6, 4.58, 4.55, 4.53, 4.52, 4.52, 4.5, 4.5]),
  }),
} as unknown as PlayerMovementData;
